/**
 * Lazy client-side loader for the vidyut-prakriya WebAssembly grammar engine.
 *
 * vidyut-prakriya implements 2000+ rules of Pāṇini's Aṣṭādhyāyī and is verified
 * against the Kāśikā and Siddhānta-Kaumudī. We use it as the *generator*: given a
 * prātipadika plus its lexical features, it derives the surface form together with
 * the full prakriyā (derivation), each step tagged with its sūtra.
 *
 * The module is ESM-only and resolves its .wasm through `import.meta.url`, so it
 * must never be evaluated during SSR. Everything here is behind a dynamic import
 * that only runs in the browser, and only when a declension is actually requested.
 */

import { CaseName, Gender, Num } from "./types";
import { applyUnadiSatva } from "./satva";

/** A single step of a derivation: the sūtra that fired and the resulting terms. */
export type PrakriyaStep = {
    /** Sūtra reference, e.g. "7.1.9". */
    code: string;
    /** Which text the rule came from, e.g. "Ashtadhyayi". */
    source: string;
    /** Term sequence after the rule applied, e.g. ["deva", "Es"]. */
    terms: string[];
};

/** One derived form plus the derivation that produced it. */
export type DerivedForm = {
    /** Surface form in IAST. */
    text: string;
    /** Surface form in SLP1, as the engine emits it. */
    slp1: string;
    steps: PrakriyaStep[];
};

/**
 * How a stem should be handed to the engine. Sanskrit declension is not
 * predictable from spelling alone, so this carries the lexical facts that
 * spelling hides — see `annotation.ts` for where they come from.
 */
export type StemAnalysis =
    /** An ordinary prātipadika: deva, agni, rājan, manas. */
    | { kind: "basic"; stem: string }
    /** A feminine already ending in a nyāp affix (ṭāp/ṅīp): sītā, nadī, devī. */
    | { kind: "nyap"; stem: string }
    /**
     * A kṛdanta — a stem derived from a verbal root. This covers root-nouns
     * (√bhū + kvip → bhū), agent nouns (√kṛ + tṛc → kartṛ) and the -is/-us
     * stems formed with an unādi affix (√hu + isi → havis), all of which
     * decline differently from same-looking plain stems.
     *
     * Exactly one of `krt` or `unadi` is given.
     */
    | {
        kind: "krdanta";
        root: string;
        gana: Gana;
        krt?: string;
        unadi?: string;
        prefixes?: string[];
      }
    /**
     * A compound, declined through its final member.
     *
     * A tatpuruṣa or bahuvrīhi takes its declension from the last member; the
     * earlier ones are carried along unchanged. *su-dhī* declines exactly as
     * *dhī* does, giving *sudhiyau* rather than the *sudhyau* an opaque stem
     * would produce, because the final member is a verbal root and 6.4.77
     * applies to it.
     *
     * This is how compounds are handled here because neither the WebAssembly
     * bindings nor the upstream ones expose a samāsa constructor, so the whole
     * compound cannot be described to the engine directly.
     */
    | { kind: "compound"; prefix: string; final: StemAnalysis };

export type Gana =
    | "Bhvadi" | "Adadi" | "Juhotyadi" | "Divadi" | "Svadi" | "Tudadi"
    | "Rudhadi" | "Tanadi" | "Kryadi" | "Curadi" | "Kandvadi";

const LINGA: Record<Gender, "Pum" | "Stri" | "Napumsaka"> = {
    mas: "Pum",
    fem: "Stri",
    neu: "Napumsaka",
};

const VIBHAKTI: Record<CaseName, string> = {
    nom: "Prathama",
    acc: "Dvitiya",
    ins: "Trtiya",
    dat: "Caturthi",
    abl: "Panchami",
    gen: "Sasthi",
    loc: "Saptami",
    voc: "Sambodhana",
};

const VACANA: Record<Num, "Eka" | "Dvi" | "Bahu"> = {
    s: "Eka",
    d: "Dvi",
    p: "Bahu",
};

/**
 * SLP1 → IAST. SLP1 is one character per phoneme, which is why the engine works
 * in it internally; we convert only at the boundary.
 */
const SLP1_TO_IAST: Record<string, string> = {
    A: "ā", I: "ī", U: "ū",
    f: "ṛ", F: "ṝ", x: "ḷ", X: "ḹ",
    E: "ai", O: "au", M: "ṃ", H: "ḥ",
    K: "kh", G: "gh", N: "ṅ",
    C: "ch", J: "jh", Y: "ñ",
    W: "ṭh", Q: "ḍh", R: "ṇ", w: "ṭ", q: "ḍ",
    T: "th", D: "dh",
    P: "ph", B: "bh",
    S: "ś", z: "ṣ",
};

/** IAST → SLP1. Longest sources first so digraphs win over their prefixes. */
const IAST_TO_SLP1: Array<[string, string]> = [
    ["ai", "E"], ["au", "O"],
    ["kh", "K"], ["gh", "G"], ["ch", "C"], ["jh", "J"],
    ["ṭh", "W"], ["ḍh", "Q"], ["th", "T"], ["dh", "D"],
    ["ph", "P"], ["bh", "B"],
    ["ā", "A"], ["ī", "I"], ["ū", "U"],
    ["ṛ", "f"], ["ṝ", "F"], ["ḷ", "x"], ["ḹ", "X"],
    ["ṃ", "M"], ["ḥ", "H"],
    ["ṅ", "N"], ["ñ", "Y"], ["ṇ", "R"],
    ["ṭ", "w"], ["ḍ", "q"],
    ["ś", "S"], ["ṣ", "z"],
];

export const slp1ToIast = (s: string): string =>
    s.split("").map(c => (c in SLP1_TO_IAST ? SLP1_TO_IAST[c] : c)).join("");

/**
 * Normalises the spellings that differ between sources without differing in
 * pronunciation, so forms can be compared meaningfully.
 *
 * Anusvāra is written ṁ (U+1E41) by some editors and ṃ (U+1E43) by others —
 * *sucakṣūṁṣi* and *sucakṣūṃṣi* are the same word. Candrabindu is folded in too.
 */
export const normalizeIast = (s: string): string =>
    s.normalize("NFC").replace(/[ṁ̐ँ]/g, "ṃ");

export const iastToSlp1 = (s: string): string => {
    const normalized = s.normalize("NFC");
    let out = "";
    let i = 0;
    outer: while (i < normalized.length) {
        for (const [src, dst] of IAST_TO_SLP1) {
            if (normalized.startsWith(src, i)) {
                out += dst;
                i += src.length;
                continue outer;
            }
        }
        out += normalized[i];
        i += 1;
    }
    return out;
};

/* ------------------------------------------------------------------ loading */

type VidyutModule = typeof import("@siva-sh/vidyut");

let loadPromise: Promise<Vyakarana | null> | null = null;
let engine: Vyakarana | null = null;

/** Minimal shape of the engine handle we hold onto. */
type Vyakarana = {
    deriveSubantas(args: unknown): Array<{
        text: string;
        history: Array<{ rule: { code: string; source: string }; result: Array<{ text: string }> }>;
    }>;
};

const isNode = (): boolean =>
    typeof process !== "undefined" &&
    process.versions != null &&
    process.versions.node != null &&
    typeof window === "undefined";

/**
 * Under Node (Jest, and any build-time use) there is no fetch target for the
 * .wasm, so we hand the bytes to `initSync` directly. `require` is resolved
 * indirectly so that webpack does not try to bundle `fs` into the browser build.
 */
const initUnderNode = async (vidyut: VidyutModule): Promise<void> => {
    // eslint-disable-next-line no-eval
    const nodeRequire = eval("require") as ((id: string) => any) & { resolve(id: string): string };
    const fs = nodeRequire("fs");
    const path = nodeRequire("path");

    // The package's `exports` map does not expose package.json, so resolve the
    // main entry (browser/vidyut.js) and take the .wasm sitting beside it.
    const candidates: string[] = [];
    try {
        candidates.push(path.join(path.dirname(nodeRequire.resolve("@siva-sh/vidyut")), "vidyut_bg.wasm"));
    } catch {
        /* fall through to the cwd-relative guess below */
    }
    candidates.push(
        path.join(process.cwd(), "node_modules", "@siva-sh", "vidyut", "browser", "vidyut_bg.wasm"),
    );

    const wasmPath = candidates.find((p: string) => fs.existsSync(p));
    if (!wasmPath) {
        throw new Error(`vidyut: could not locate vidyut_bg.wasm (tried ${candidates.join(", ")})`);
    }
    vidyut.initSync({ module: fs.readFileSync(wasmPath) });
};

/**
 * Loads the WASM engine once and caches it. Returns null if the module fails to
 * load, so callers can degrade gracefully rather than crash the page.
 */
export const loadEngine = (): Promise<Vyakarana | null> => {
    if (loadPromise) return loadPromise;

    loadPromise = (async () => {
        try {
            const vidyut: VidyutModule = await import("@siva-sh/vidyut");
            if (isNode()) {
                await initUnderNode(vidyut);
            } else {
                await vidyut.default();
            }
            engine = new vidyut.Vyakarana() as unknown as Vyakarana;
            return engine;
        } catch (e) {
            console.error("vidyut: failed to load grammar engine", e);
            return null;
        } finally {
            // A failed load must not be remembered. The .wasm is fetched while
            // the page is also pulling down ~90 MB of dictionaries, and a
            // transient abort is entirely possible; caching that would leave
            // declension silently dead for the rest of the session, showing
            // "no results" rather than an error. Clearing the promise lets the
            // next call try again.
            if (!engine) loadPromise = null;
        }
    })();

    return loadPromise;
};

/** True once the engine is resident, so callers can skip a loading state. */
export const isEngineReady = (): boolean => loadPromise !== null;

/* --------------------------------------------------------------- generation */

/** Builds the `pratipadika` argument the engine expects from our analysis. */
const toPratipadikaArgs = (analysis: StemAnalysis): unknown => {
    switch (analysis.kind) {
        case "compound":
            // Never reached: a compound is handled by deriving its final member.
            return toPratipadikaArgs(analysis.final);
        case "basic":
            return { basic: iastToSlp1(analysis.stem) };
        case "nyap":
            return { nyap: iastToSlp1(analysis.stem) };
        case "krdanta": {
            const dhatu = {
                // Roots are stored in aupadeśika form, which is already SLP1 and
                // carries `it` markers that must survive untouched.
                aupadeshika: analysis.root,
                gana: analysis.gana,
                sanadi: [] as string[],
                prefixes: analysis.prefixes ?? [],
            };
            return analysis.unadi
                ? { krdanta: { dhatu, unadi: analysis.unadi } }
                : { krdanta: { dhatu, krt: analysis.krt } };
        }
    }
};

/**
 * Derives every form for one cell of the paradigm. Sanskrit legitimately admits
 * more than one form in some cells (e.g. abl. sg. of deva is devāt or devād), so
 * this returns a list rather than a single string.
 */
export const deriveCell = async (
    analysis: StemAnalysis,
    gender: Gender,
    caseName: CaseName,
    num: Num,
): Promise<DerivedForm[]> => {
    // A compound is derived through its final member, then the earlier members
    // are put back in front of every form.
    if (analysis.kind === "compound") {
        const inner = await deriveCell(analysis.final, gender, caseName, num);
        return inner.map(f => ({
            ...f,
            text: analysis.prefix + f.text,
            slp1: iastToSlp1(analysis.prefix) + f.slp1,
        }));
    }

    const engine = await loadEngine();
    if (!engine) return [];

    let prakriyas;
    try {
        prakriyas = engine.deriveSubantas({
            pratipadika: toPratipadikaArgs(analysis),
            linga: LINGA[gender],
            vibhakti: VIBHAKTI[caseName],
            vacana: VACANA[num],
        });
    } catch (e) {
        console.warn("vidyut: derivation failed", analysis, gender, caseName, num, e);
        return [];
    }

    const seen = new Set<string>();
    const forms: DerivedForm[] = [];
    for (const p of prakriyas) {
        if (seen.has(p.text)) continue;
        seen.add(p.text);

        // A stem handed over as an opaque string carries no affix boundary, so
        // the engine cannot apply 8.3.57 to it. See `satva.ts`.
        const iast = analysis.kind === "basic"
            ? applyUnadiSatva(slp1ToIast(p.text), analysis.stem)
            : slp1ToIast(p.text);

        forms.push({
            text: iast,
            slp1: p.text,
            steps: p.history.map(step => ({
                code: step.rule.code,
                source: step.rule.source,
                terms: step.result
                    .map(t => t.text)
                    .filter(t => t.length > 0)
                    .map(slp1ToIast),
            })),
        });
    }
    return forms;
};

export const CASE_ORDER: CaseName[] = ["nom", "voc", "acc", "ins", "dat", "abl", "gen", "loc"];
export const NUM_ORDER: Num[] = ["s", "d", "p"];

/** A full 8x3 paradigm. `cells[caseName][num]` holds every attested form. */
export type Paradigm = {
    analysis: StemAnalysis;
    gender: Gender;
    cells: Record<CaseName, Record<Num, DerivedForm[]>>;
};

/** Derives the complete 8x3 table for a stem in one gender. */
export const declineParadigm = async (
    analysis: StemAnalysis,
    gender: Gender,
): Promise<Paradigm> => {
    const cells = {} as Record<CaseName, Record<Num, DerivedForm[]>>;
    for (const caseName of CASE_ORDER) {
        cells[caseName] = {} as Record<Num, DerivedForm[]>;
        for (const num of NUM_ORDER) {
            cells[caseName][num] = await deriveCell(analysis, gender, caseName, num);
        }
    }
    return { analysis, gender, cells };
};
